import { useEffect, useMemo, useState, type FormEvent } from 'react';
import type { Question } from '../features/surveys/api.ts';
import { SurveyAnswerInput } from './SurveyAnswerInput.tsx';

function isBlank(v: unknown): boolean {
  return v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
}

/**
 * The reusable answer form shared by field collection (blank) and admin editing
 * (pre-filled). Owns skip-logic visibility, per-question inputs, the answered
 * progress bar, required validation + scroll-to-first-missing, PRIORITY seeding,
 * and building the visible-only answers payload passed to `onSubmit`.
 */
export function SurveyAnswerForm({
  questions,
  initialAnswers,
  submitLabel,
  onSubmit,
  pending,
}: {
  questions: Question[];
  initialAnswers: Record<string, unknown>;
  submitLabel: string;
  onSubmit: (answers: Record<string, unknown>) => void;
  pending?: boolean;
}): JSX.Element {
  const [answers, setAnswers] = useState<Record<string, unknown>>(initialAnswers);
  const [clientError, setClientError] = useState<string | null>(null);
  const [missing, setMissing] = useState<Set<string>>(new Set());

  // Skip logic: a question is visible only when its trigger is itself visible
  // AND the trigger's current answer matches. Questions arrive in order, so a
  // single forward pass resolves chains. Hidden questions don't count toward
  // progress or the required check, and their (stale) answers are never sent.
  const visibleQuestions = useMemo(() => {
    const visibleIds = new Set<string>();
    return questions.filter((q) => {
      const vis = !q.condition
        || (visibleIds.has(q.condition.questionId) && answers[q.condition.questionId] === q.condition.equals);
      if (vis) visibleIds.add(q.id);
      return vis;
    });
  }, [questions, answers]);
  const answeredCount = useMemo(
    () => visibleQuestions.filter((q) => !isBlank(answers[q.id])).length,
    [visibleQuestions, answers],
  );

  // PRIORITY questions always carry a full ranking — seed each not-yet-answered
  // one with the admin's item order so there's a valid answer from the start,
  // without clobbering answers already provided (initial or in-progress).
  useEffect(() => {
    setAnswers((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const q of questions) {
        if (q.type === 'PRIORITY' && q.options && !(q.id in next)) { next[q.id] = [...q.options]; changed = true; }
      }
      return changed ? next : prev;
    });
  }, [questions]);

  // Wrap state changes so any edit clears the prior error banner + that field's
  // "required" highlight — immediate feedback while filling in.
  const setAnswer = (id: string, v: unknown): void => {
    setAnswers((prev) => ({ ...prev, [id]: v }));
    if (missing.has(id)) {
      setMissing((prev) => { const next = new Set(prev); next.delete(id); return next; });
    }
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    const missingIds = visibleQuestions.filter((q) => q.required && isBlank(answers[q.id])).map((q) => q.id);
    if (missingIds.length > 0) {
      setMissing(new Set(missingIds));
      setClientError(`Please answer ${missingIds.length} required question${missingIds.length > 1 ? 's' : ''} highlighted below.`);
      document.getElementById(`q-${missingIds[0]}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setMissing(new Set());
    setClientError(null);
    const visibleIds = new Set(visibleQuestions.map((q) => q.id));
    const payload = Object.fromEntries(Object.entries(answers).filter(([id]) => visibleIds.has(id)));
    onSubmit(payload);
  };

  const visibleCount = visibleQuestions.length;
  const progress = visibleCount ? Math.round((answeredCount / visibleCount) * 100) : 0;

  return (
    <div className="space-y-5">
      {visibleCount > 0 && (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="status-label !text-foreground">Progress</span>
            <span className="mono-meta tabular text-muted-foreground">{answeredCount} of {visibleCount} answered</span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
            <div className="h-full rounded-full bg-primary transition-all duration-300" style={{ width: `${progress}%` }} />
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {questions.length === 0 && (
          <p className="card-paper p-6 text-center text-sm text-muted-foreground">
            This survey has no questions yet.
          </p>
        )}

        {visibleQuestions.map((q, i) => {
          const isMissing = missing.has(q.id);
          return (
            <section
              key={q.id}
              id={`q-${q.id}`}
              className={`card-paper space-y-3 p-4 sm:p-5 transition-shadow ${isMissing ? 'ring-2 ring-destructive/50' : ''}`}
            >
              <div className="flex items-baseline gap-3">
                <span className="mono-meta flex-none pt-0.5 text-muted-foreground">Q{i + 1}</span>
                <div className="flex-1">
                  <label className="block text-base font-medium leading-snug text-ink">
                    {q.label}
                  </label>
                  {q.required && (
                    <span className={`status-label ${isMissing ? '!text-destructive' : ''}`}>Required</span>
                  )}
                </div>
              </div>
              <SurveyAnswerInput
                question={q}
                value={answers[q.id]}
                onChange={(v) => setAnswer(q.id, v)}
                disabled={pending}
              />
              {isMissing && (
                <p className="text-sm text-destructive" role="alert">Please answer this question.</p>
              )}
            </section>
          );
        })}

        {clientError && (
          <p className="text-sm text-destructive" role="alert">{clientError}</p>
        )}

        {visibleCount > 0 && (
          <button
            type="submit"
            disabled={pending}
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-5 py-3.5 text-base font-semibold text-primary-foreground shadow-[0_1px_0_hsl(var(--accent-ink)/0.1),0_0_0_1px_hsl(var(--accent-ink)/0.05)] transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {pending ? 'Saving…' : submitLabel}
          </button>
        )}
      </form>
    </div>
  );
}
